import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Space, Button, Form, message, Row, Col, Input, Divider, DatePicker, Table, TreeSelect } from 'antd';
import { isMobile } from 'react-device-detect';
import BaseRadioList from '@/components/Elements/BaseRadios';
import BaseSelect from '@/components/Elements/BaseSelect';
import moment from 'moment';
import { getProcedureActive } from '@/redux/actions/procedureActions';
import { getSpecificInstructionTemplate, getInstructionTemplateActive } from "@/redux/actions/instructionTemplateActions";
import { getSpecificPrescriptionTemplate, getPrescriptionTemplateActive } from "@/redux/actions/prescriptionTemplateActions";
import { _newLine, makeTree } from '@/utils/helpers';
import { SaveOutlined } from '@ant-design/icons';
import { getCustomerServices } from '@/redux/actions/customerServicesActions';
import { createDirectTreatmentSession, updateTreatmentSession } from '@/redux/actions/treatmentSessionActions';

const TreatmentSessionCreateDirectForm = (props) => {
    const { visible, onCancel, customerId, customer, record, onSuccess } = props;
    const [form] = Form.useForm();
    const formRef = React.createRef();
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();
    const doctors = useSelector((state) => state.config.doctors) || [];
    const assistants = useSelector((state) => state.config.assistants) || [];
    const procedures = useSelector(state => state.procedureReducer.procedureActiveList);
    const prescriptions = useSelector(state => state.prescriptionTemplateReducer.prescriptionTemplateActiveList);
    const instructions = useSelector(state => state.instructionTemplateReducer.instructionTemplateActiveList);
    const customer_services = useSelector(state => state.customerServicesReducer.customer_services);

    //console.log('customer_services: ', customer_services);

    const procedureParentTree = makeTree(procedures || [], 0);

    const [selectedProcedures, setSelectedProcedures] = useState([]);

    useEffect(() => {
        dispatch(getProcedureActive());
        dispatch(getPrescriptionTemplateActive());
        dispatch(getInstructionTemplateActive());
        dispatch(getCustomerServices(customerId));
    }, [dispatch]);

    useEffect(() => {
        if (record) {
            // convert ngày / giờ nếu có
            const values = {
                ...record,
                ngay: record.ngay ? moment(record.ngay, "YYYY-MM-DD") : null,
                bac_si_chiu_trach_nhiem_buoi_hen_ids: record.doctors?.map(d => d.doctor_id) || [],
                y_ta_phu_trach_ids: record.assistants?.map(a => a.assistant_id) || [],
                customer_service_ids: record.customer_services?.map(s => s.customer_service_id) || [],
                loi_dan: _newLine(record.loi_dan),
                don_thuoc: _newLine(record.don_thuoc),
                chan_doan_dieu_tri: _newLine(record.chan_doan_dieu_tri),
                trieu_chung_huong_dieu_tri: _newLine(record.trieu_chung_huong_dieu_tri),
                ghi_chu_cho_khach_hang: _newLine(record.ghi_chu_cho_khach_hang),
                ghi_chu_noi_bo: _newLine(record.ghi_chu_noi_bo),
            };

            form.setFieldsValue(values);

            // đồng bộ selectedProcedures để hiển thị Table
            setSelectedProcedures(record.procedures || []);
        } else {
            form.resetFields();
            setSelectedProcedures([]);
        }
    }, [record, form]);


    const initValues = {
        branch_id: customer?.branch_id || null,
        bac_si_chiu_trach_nhiem_buoi_hen_ids: [],
        y_ta_phu_trach_ids: [],
        ngay: null, // moment(), // default hôm nay
        thu_thuat_ids: [],
        mau_don_thuoc: null,
        mau_loi_dan: null,
        bac_si_chiu_trach_nhiem: null,
        customer_service_ids: [],
        schedule_id: null,
    };

    const handleSubmit = (values) => {
        setIsLoading(true);

        // cleanup field không cần thiết
        delete values.thu_thuat_ids;
        delete values.mau_don_thuoc;
        delete values.mau_loi_dan;

        // thêm thủ thuật đã chọn
        values.procedures = selectedProcedures;
        values.customer_id = customerId;
        values.branch_id = customer?.branch_id || null;
        values.bac_si_chiu_trach_nhiem_buoi_hen_ids = values.bac_si_chiu_trach_nhiem_buoi_hen_ids || [];


        console.log('values: ', values);
        // nếu đang update thì thêm id vào payload
        if (record && record.id) {
            values.id = record.id;
            dispatch(updateTreatmentSession(record.id, values))   // gọi action update
                .then(() => {
                    if (onSuccess) onSuccess();  // gọi callback
                    handleClose();
                })
                .catch(() => setIsLoading(false));
        } else {
            dispatch(createDirectTreatmentSession(values))              // gọi action create
                .then(() => {
                    if (onSuccess) onSuccess();  // gọi callback
                    handleClose();
                })
                .catch(() => setIsLoading(false));
        }
    };


    const handleClose = () => {
        form.resetFields();
        onCancel();
        setSelectedProcedures([]);
        setIsLoading(false);
    };

    const handleError = () => {
        message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    };

    const handlePrescriptionChange = (value) => {
        dispatch(getSpecificPrescriptionTemplate(value)).then(res => {
            form.setFieldsValue({ don_thuoc: _newLine(res?.content) });
        });
    };

    const handleInstructionChange = (value) => {
        dispatch(getSpecificInstructionTemplate(value)).then(res => {
            form.setFieldsValue({ loi_dan: _newLine(res?.content) });
        });
    };

    const handleSelectProcedure = (value) => {
        const procedure = procedures.find(item => item.id === value);
        if (!procedure) return;

        setSelectedProcedures(prev => {
            if (prev.some(item => item.id === procedure.id)) return prev;

            return [
                ...prev,
                {
                    id: procedure.id,
                    name: procedure.name,
                    description: procedure.description || '',
                }
            ];
        });
    };

    const handleRemoveProcedure = (id) => {
        setSelectedProcedures(prev => prev.filter(item => item.id !== id));
    };

    const handleProcedureDescriptionChange = (id, value) => {
        setSelectedProcedures(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, description: value }
                    : item
            )
        );
    };


    const columns = [
        {
            title: "Tên thủ thuật",
            dataIndex: "name",
            key: "name",
            //width: "300px",
        },
        {
            title: "Nội dung",
            dataIndex: "description",
            key: "description",
            width: "50%",
            render: (_, record) => (
                <Input.TextArea
                    rows={5}
                    value={record.description}
                    placeholder="Nhập / chỉnh sửa nội dung thủ thuật"
                    onChange={(e) =>
                        handleProcedureDescriptionChange(record.id, e.target.value)
                    }
                />
            ),
        },
        {
            title: "#",
            key: "action",
            width: "40px",
            align: "center",
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveProcedure(record.id)}
                >
                    Xóa
                </Button>
            ),
        }
    ];

    const disableNodesWithChildren = (nodes = []) => {
        return nodes.map(node => ({
            ...node,
            disabled: Array.isArray(node.children) && node.children.length > 0, // disable nếu có con
            children: disableNodesWithChildren(node.children || []) // đệ quy xuống các tầng con
        }));
    };


    return (
        <div>
            <Drawer
                visible={visible}
                title={record ? "Cập nhật phiếu điều trị" : "Thêm điều trị mới"}
                onClose={handleClose}
                closable
                maskClosable={false}
                width={isMobile ? '100%' : '80%'}
                forceRender
                footer={
                    <div style={{ textAlign: "right" }}>
                        <Space>
                            <Button icon={<SaveOutlined />} size='large' loading={isLoading} type="primary" onClick={() => form.submit()}>
                                Lưu
                            </Button>
                        </Space>
                    </div>
                }
            >
                {visible && (
                    <Form
                        form={form}
                        layout="vertical"
                        className='form-customer'
                        initialValues={initValues}
                        ref={formRef}
                        onFinish={handleSubmit}
                        onFinishFailed={handleError}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label="Lần khám" name="lan_kham" rules={[{ required: true, message: 'Bắt buộc chọn' }]}>
                                    <BaseRadioList
                                        options={[
                                            { label: 'Điều trị mới', value: 'dieu_tri_moi' },
                                            { label: 'Tái khám', value: 'tai_kham' }
                                        ]}
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày điều trị" name="ngay" rules={[{ required: true, message: "Bắt buộc chọn" }]}>
                                    <DatePicker
                                        className="w-100"
                                        format="DD/MM/YYYY"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Bác sĩ phụ trách"
                                    name="bac_si_chiu_trach_nhiem_buoi_hen_ids"
                                    rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        mode="multiple"
                                        options={doctors || []}
                                        optionValue="user_id"
                                        optionLabel="display_name"
                                        showSearch
                                        placeholder="Chọn bác sĩ"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Y tá phụ trách"
                                    name="y_ta_phu_trach_ids"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        mode="multiple"
                                        options={assistants || []}
                                        optionValue="user_id"
                                        optionLabel="display_name"
                                        showSearch
                                        placeholder="Chọn một hoặc nhiều y tá"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={24}>
                                <Form.Item label="Ghi chú nội bộ" name="ghi_chu_noi_bo" rules={[{ required: false, message: 'Bắt buộc nhập' }]}>
                                    <Input.TextArea rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" orientationMargin={0}>
                            CÁC ĐIỀU TRỊ / THỦ THUẬT
                        </Divider>
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Dịch vụ"
                                    name="customer_service_ids"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        mode="multiple"
                                        options={customer_services?.options || []}
                                        optionValue="id"
                                        optionLabel="label"
                                        showSearch
                                        placeholder="Chọn một hoặc nhiều dịch vụ"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Thủ thuật"
                                    name="thu_thuat_ids"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <TreeSelect
                                        style={{ width: '100%' }}
                                        dropdownStyle={{ maxHeight: 450, overflow: 'auto' }}
                                        treeData={disableNodesWithChildren(procedureParentTree || [])}
                                        showSearch
                                        multiple={false}
                                        treeNodeFilterProp="title"
                                        allowClear
                                        placeholder="Chọn một bản ghi"
                                        treeDefaultExpandAll
                                        onChange={(value) => handleSelectProcedure(value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Table
                                    rowKey="id"
                                    dataSource={selectedProcedures}
                                    columns={columns}
                                    pagination={false}
                                    bordered
                                    size="small"
                                    locale={{ emptyText: 'Chưa có nội dung' }}
                                />
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin={0}>
                            ĐƠN THUỐC
                        </Divider>
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Mẫu đơn thuốc"
                                    name="mau_don_thuoc"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        options={prescriptions || []}
                                        optionValue="id"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn mẫu đơn thuốc"
                                        className="w-100"
                                        onChange={(value) => handlePrescriptionChange(value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Đơn thuốc" name="don_thuoc" rules={[{ required: false, message: 'Bắt buộc nhập' }]}>
                                    <Input.TextArea rows={10} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" orientationMargin={0}>
                            LỜI DẶN
                        </Divider>
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Mẫu lời dặn"
                                    name="mau_loi_dan"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        options={instructions || []}
                                        optionValue="id"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn mẫu lời dặn"
                                        className="w-100"
                                        onChange={(value) => handleInstructionChange(value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Lời dặn" name="loi_dan" rules={[{ required: false, message: 'Bắt buộc nhập' }]}>
                                    <Input.TextArea rows={10} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Drawer>
        </div>
    );
}

export default TreatmentSessionCreateDirectForm;
